import React, { useEffect, useRef, useState } from "react";
import { Input, Popover, PopoverBody, Table, Spinner } from "reactstrap";
import StoreMultiSelectFilter from "../components/StoreMultiSelectFilter";
import {
  activityCountPhrase,
  buyerDashboardNotificationName,
  BUYER_ENGAGEMENT_STATUS_OPTIONS,
  CONTACT_SUB_STATUS_ENQUIRED,
  CONTACT_SUB_STATUS_MARKETED,
  CONTACT_SUB_STATUS_NONE,
  CONTACT_SUB_STATUS_NOT_INTERESTED,
  CONTACT_SUB_STATUS_REJECTED,
  copyTextToClipboard,
  ensureArray,
  formatCustomerOrderMonthHeader,
  formatMobileDisplay,
  fmtHoverDate,
  fmtHoverDateTime,
  fmtOrderRupee,
  fmtRelativeTime,
  projectCountPhrase,
  sessionCountPhrase,
  STORE_DASHBOARD_FILTER_INPUT_CLASS,
  STORE_BUYER_FIT_FILTER_OPTIONS,
  STORE_BUYER_CONVICTION_FILTER_OPTIONS,
  whatsappUrlFromMobile,
} from "./storeSalesDashboardHelpers";

function BuyerRemarksCell({ salesNotes }) {
  const notes = Array.isArray(salesNotes) ? salesNotes : [];
  if (notes.length === 0) return "-";

  return (
    <>
      {notes.map((note, index) => {
        const author = String(note?.author_name || "").trim();
        const when = note?.created_at ? fmtHoverDateTime(note.created_at) : "";
        const isLast = index === notes.length - 1;
        return (
          <div
            key={note?.id ?? `${when}-${note?.body}`}
            className={isLast ? undefined : "mb-2 pb-2 border-bottom"}
          >
            <div className="text-muted mb-1">
              {when}
              {author ? ` · ${author}` : ""}
            </div>
            <div className="text-break">{note?.body}</div>
          </div>
        );
      })}
    </>
  );
}

function CustomerOrderHeaderTotal({ bucket, loading }) {
  if (loading) {
    return <span>…</span>;
  }

  const value = Number(bucket?.value || 0);

  return <span className="fw-bold">{fmtOrderRupee(value)}</span>;
}

function CustomerOrderValueCell({ bucket }) {
  const count = Number(bucket?.count || 0);
  const value = Number(bucket?.value || 0);

  if (count === 0) {
    return <span className="text-white">-</span>;
  }

  return (
    <div className="d-inline-flex align-items-center justify-content-center gap-1 fw-bold">
      {count > 1 ? (
        <span className="badge rounded-pill border text-white bg-light small px-2">
          {count}
        </span>
      ) : null}
      <span>{fmtOrderRupee(value)}</span>
    </div>
  );
}

export function BuyerEditModalMobileField({ mobile }) {
  const [mobilePopoverOpen, setMobilePopoverOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const copiedTimeoutRef = useRef(null);
  const mobileRaw = String(mobile || "").trim();
  const mobileCopyable =
    mobileRaw.length > 0 && mobileRaw !== "—" && mobileRaw !== "-";
  const mobileTargetId = "buyer-edit-modal-mobile";

  useEffect(() => {
    return () => {
      if (copiedTimeoutRef.current) {
        window.clearTimeout(copiedTimeoutRef.current);
      }
    };
  }, []);

  const handleCopyMobile = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!mobileCopyable) return;
    const ok = await copyTextToClipboard(mobileRaw);
    if (!ok) return;
    setCopied(true);
    if (copiedTimeoutRef.current) {
      window.clearTimeout(copiedTimeoutRef.current);
    }
    copiedTimeoutRef.current = window.setTimeout(() => setCopied(false), 2000);
  };

  const toggleMobilePopover = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setMobilePopoverOpen((open) => !open);
  };

  return (
    <div className="mb-3">
      <div className="small text-muted mb-1">Mobile</div>
      <div className="form-control form-control-sm bg-light d-flex align-items-center">
        {mobileCopyable ? (
          <>
            <button
              type="button"
              id={mobileTargetId}
              className="btn btn-sm p-0 lh-1 text-body border-0 bg-transparent shadow-none text-decoration-none"
              title="Show mobile"
              aria-label="Show mobile"
              onClick={toggleMobilePopover}
              onMouseDown={(e) => e.stopPropagation()}
            >
              {mobileRaw}
            </button>
            <Popover
              placement="right"
              isOpen={mobilePopoverOpen}
              target={mobileTargetId}
              toggle={() => setMobilePopoverOpen(false)}
            >
              <PopoverBody>
                <div className="d-flex align-items-center gap-2">
                  <span className="fs-2">{formatMobileDisplay(mobileRaw)}</span>
                  <button
                    type="button"
                    className="btn btn-link p-0 lh-1 text-muted"
                    title="Copy mobile"
                    aria-label="Copy mobile"
                    onClick={handleCopyMobile}
                    onMouseDown={(e) => e.stopPropagation()}
                  >
                    <i className="fa-regular fa-copy" aria-hidden="true" />
                  </button>
                  {copied ? (
                    <span className="text-success small">copied</span>
                  ) : null}
                  <button
                    type="button"
                    className="btn-close ms-1"
                    aria-label="Close"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setMobilePopoverOpen(false);
                    }}
                    onMouseDown={(e) => e.stopPropagation()}
                  />
                </div>
              </PopoverBody>
            </Popover>
          </>
        ) : (
          <span className="text-muted">—</span>
        )}
      </div>
    </div>
  );
}

export function BuyerEditModalUserBar({ userName, mobile }) {
  const [mobilePopoverOpen, setMobilePopoverOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const copiedTimeoutRef = useRef(null);
  const displayName = String(userName || "").trim();
  const mobileRaw = String(mobile || "").trim();
  const mobileCopyable =
    mobileRaw.length > 0 && mobileRaw !== "—" && mobileRaw !== "-";
  const mobileTargetId = "buyer-edit-modal-mobile";

  useEffect(() => {
    return () => {
      if (copiedTimeoutRef.current) {
        window.clearTimeout(copiedTimeoutRef.current);
      }
    };
  }, []);

  const handleCopyMobile = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!mobileCopyable) return;
    const ok = await copyTextToClipboard(mobileRaw);
    if (!ok) return;
    setCopied(true);
    if (copiedTimeoutRef.current) {
      window.clearTimeout(copiedTimeoutRef.current);
    }
    copiedTimeoutRef.current = window.setTimeout(() => setCopied(false), 2000);
  };

  const toggleMobilePopover = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setMobilePopoverOpen((open) => !open);
  };

  return (
    <div className="border rounded-3 bg-light p-3 mb-3 d-flex w-100 align-items-center">
      <div className="w-50 fw-semibold text-truncate pe-2">
        {displayName || "—"}
      </div>
      <div className="w-50 text-end text-muted text-truncate ps-2">
        {mobileCopyable ? (
          <>
            <button
              type="button"
              id={mobileTargetId}
              className="btn btn-sm p-0 lh-1 text-muted border-0 bg-transparent shadow-none text-decoration-none"
              title="Show mobile"
              aria-label="Show mobile"
              onClick={toggleMobilePopover}
              onMouseDown={(e) => e.stopPropagation()}
            >
              {mobileRaw}
            </button>
            <Popover
              placement="right"
              isOpen={mobilePopoverOpen}
              target={mobileTargetId}
              toggle={() => setMobilePopoverOpen(false)}
            >
              <PopoverBody>
                <div className="d-flex align-items-center gap-2">
                  <span className="fs-2">{formatMobileDisplay(mobileRaw)}</span>
                  <button
                    type="button"
                    className="btn btn-link p-0 lh-1 text-muted"
                    title="Copy mobile"
                    aria-label="Copy mobile"
                    onClick={handleCopyMobile}
                    onMouseDown={(e) => e.stopPropagation()}
                  >
                    <i className="fa-regular fa-copy" aria-hidden="true" />
                  </button>
                  {copied ? (
                    <span className="text-success small">copied</span>
                  ) : null}
                  <button
                    type="button"
                    className="btn-close ms-1"
                    aria-label="Close"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setMobilePopoverOpen(false);
                    }}
                    onMouseDown={(e) => e.stopPropagation()}
                  />
                </div>
              </PopoverBody>
            </Popover>
          </>
        ) : (
          <span>—</span>
        )}
      </div>
    </div>
  );
}

export function BuyerDashboardNameCell({ row, showNotifications, onOpenNotifications }) {
  const [mobilePopoverOpen, setMobilePopoverOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const copiedTimeoutRef = useRef(null);
  const notifyName = buyerDashboardNotificationName(row);
  const dealerName = String(row.dealerName || "").trim();
  const hasDealerName = dealerName.length > 0 && dealerName !== "-";
  const firstUserName = String(row.firstUserName || "").trim();
  const mobile = String(row.firstUserMobile || "").trim();
  const mobileCopyable = mobile.length > 0 && mobile !== "—" && mobile !== "-";
  const showUserName = firstUserName.length > 0 && firstUserName !== "—";
  const mobileTargetId = `buyer-dashboard-mobile-${row.id}`;
  const whatsappUrl = whatsappUrlFromMobile(mobile);
  const profileFit = row.profileFit;

  useEffect(() => {
    return () => {
      if (copiedTimeoutRef.current) {
        window.clearTimeout(copiedTimeoutRef.current);
      }
    };
  }, []);

  const handleCopyMobile = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!mobileCopyable) return;
    const ok = await copyTextToClipboard(mobile);
    if (!ok) return;
    setCopied(true);
    if (copiedTimeoutRef.current) {
      window.clearTimeout(copiedTimeoutRef.current);
    }
    copiedTimeoutRef.current = window.setTimeout(() => setCopied(false), 2000);
  };

  const toggleMobilePopover = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setMobilePopoverOpen((open) => !open);
  };

  return (
    <>
      <div className="d-flex align-items-center gap-2">
        <span className="d-inline-flex align-items-center gap-1">
          {profileFit === "fit" ? (
            <i
              className="fa-solid fa-check text-success small"
              title="Profile fit"
              aria-label="Profile fit"
            />
          ) : profileFit === "not_fit" ? (
            <i
              className="fa-solid fa-xmark text-danger small"
              title="Profile not fit"
              aria-label="Profile not fit"
            />
          ) : null}
          <span>
            {row.buyerDisplayName}{" "}
            <span className="text-muted">#{row.id}</span>
          </span>
        </span>
        {showNotifications ? (
          <button
            type="button"
            className="btn btn-sm p-0 lh-1 text-muted border-0 bg-transparent shadow-none"
            title="View notifications"
            onClick={(e) => {
              e.stopPropagation();
              onOpenNotifications?.(notifyName);
            }}
          >
            <i className="fa-regular fa-bell" aria-hidden="true" />
          </button>
        ) : null}
      </div>
      {showUserName || mobileCopyable ? (
        <div className="small text-muted mt-1 d-flex align-items-center gap-1 flex-wrap">
          {showUserName ? <span>{firstUserName}</span> : null}
          {mobileCopyable ? (
            <>
              {showUserName ? <span>/</span> : null}
              <button
                type="button"
                id={mobileTargetId}
                className="btn btn-sm p-0 lh-1 text-muted border-0 bg-transparent shadow-none text-decoration-none"
                title="Show mobile"
                aria-label="Show mobile"
                onClick={toggleMobilePopover}
                onMouseDown={(e) => e.stopPropagation()}
              >
                {mobile}
              </button>
              {whatsappUrl ? (
                <a
                  href={whatsappUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-sm p-0 lh-1 text-success border-0 bg-transparent shadow-none"
                  title="Open WhatsApp"
                  aria-label="Open WhatsApp"
                  onClick={(e) => e.stopPropagation()}
                  onMouseDown={(e) => e.stopPropagation()}
                >
                  <i className="fa-brands fa-whatsapp" aria-hidden="true" />
                </a>
              ) : null}
              <Popover
                placement="right"
                isOpen={mobilePopoverOpen}
                target={mobileTargetId}
                toggle={() => setMobilePopoverOpen(false)}
              >
                <PopoverBody>
                  <div className="d-flex align-items-center gap-2">
                    <span className="fs-2">
                      {formatMobileDisplay(mobile)}
                    </span>
                    <button
                      type="button"
                      className="btn btn-link p-0 lh-1 text-muted"
                      title="Copy mobile"
                      aria-label="Copy mobile"
                      onClick={handleCopyMobile}
                      onMouseDown={(e) => e.stopPropagation()}
                    >
                      <i className="fa-regular fa-copy" aria-hidden="true" />
                    </button>
                    {copied ? (
                      <span className="text-success small">copied</span>
                    ) : null}
                    <button
                      type="button"
                      className="btn-close ms-1"
                      aria-label="Close"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setMobilePopoverOpen(false);
                      }}
                      onMouseDown={(e) => e.stopPropagation()}
                    />
                  </div>
                </PopoverBody>
              </Popover>
            </>
          ) : null}
        </div>
      ) : (
        <div className="small text-muted mt-1">—</div>
      )}
      {hasDealerName ? (
        <div className="small text-muted mt-1">({dealerName})</div>
      ) : null}
    </>
  );
}

function BuyerProjectsCell({ row, onOpenBuyer }) {
  const count = Number(row.projectsCount || 0);

  return (
    <div className="d-flex align-items-center gap-1">
      <button
        type="button"
        className="btn btn-link btn-sm p-0 align-baseline"
        onClick={(e) => {
          e.stopPropagation();
          onOpenBuyer?.(row);
        }}
      >
        {projectCountPhrase(count)}
      </button>
      {row.hasSampleProject ? (
        <span
          className="badge rounded-circle bg-success d-inline-flex align-items-center justify-content-center flex-shrink-0"
          style={{ width: "1.125rem", height: "1.125rem", fontSize: "0.65rem" }}
          title="Has sample project"
        >
          S
        </span>
      ) : null}
    </div>
  );
}

const engagementFilterOptions = BUYER_ENGAGEMENT_STATUS_OPTIONS.map((v) => ({
  value: v,
  label: v,
}));

const StoreSalesDashboardBuyersSection = ({
  activeTab,
  buyerStateFilter,
  setBuyerStateFilter,
  buyerStateOptions,
  buyerCityFilter,
  setBuyerCityFilter,
  buyerCityOptions,
  buyerPpmFilter,
  setBuyerPpmFilter,
  buyerPpmOptions,
  buyerEngagementFilter,
  setBuyerEngagementFilter,
  buyerSalesOwnerFilter,
  setBuyerSalesOwnerFilter,
  buyerSalesOwnerFilterOptions,
  buyerContactStatusFilter,
  setBuyerContactStatusFilter,
  buyerSourceFilter,
  setBuyerSourceFilter,
  profileFitFilter,
  setProfileFitFilter,
  convictionFilter,
  setConvictionFilter,
  committedDateFilter,
  setCommittedDateFilter,
  committedDateFilterOptions,
  buyerAcquiredMonthFilter,
  setBuyerAcquiredMonthFilter,
  buyerAcquiredMonthFilterOptions,
  selectedProspectBuyerIds,
  setSelectedProspectBuyerIds,
  selectedCustomerBuyerIds,
  setSelectedCustomerBuyerIds,
  onOpenBulkEditBuyerModal,
  contactSourceOptions,
  searchText,
  setSearchText,
  buyerVisibilityFilter,
  setBuyerVisibilityFilter,
  buyersTableViewMode,
  setBuyersTableViewMode,
  displayedRows,
  highlightBuyerId,
  showStatusColumn,
  showProfileTypeColumn,
  showPpmColumn,
  showDealerColumn,
  showLeadSourceColumn,
  showEngagementColumn,
  showSalesOwnerColumn,
  showLastFollowupColumn,
  showNextFollowupColumn,
  showSessionsColumn,
  showProjectsColumn,
  showLoginAttemptsColumn,
  showRemarksInTable,
  showAcquiredColumn,
  showSampleDateColumn,
  showCustomerOrderMonthColumns,
  customerOrderMonthKeys,
  buyerOrderMonthStats,
  customerOrdersLoading,
  handleSort,
  renderSortIcon,
  handleOpenBuyer,
  openNotificationsForName,
  openActivitiesPaneForBuyer,
  openEditBuyerModal,
  contactCsvInputRef,
  onContactCsvButtonClick,
  onContactCsvFileChange,
  importCsvLoading,
  buyersLoading,
}) => {
  const showProspectPipelineFilters = activeTab === "prospects";
  const showCustomerFilters = activeTab === "customers";
  const showLeadCityFilter = activeTab === "leads";
  const signedUpColumnSortKey = activeTab === "leads" ? "createdAt" : "signedUpAt";
  const signedUpColumnLabel = activeTab === "leads" ? "Created" : "Signed up";
  const leadSourceColumnLabel = activeTab === "leads" ? "Source" : "Lead Source";
  const loginAttemptsColumnLast = activeTab === "leads";
  const showContactFilters = activeTab === "contacts";
  const showRemarksToggle = activeTab === "prospects" || activeTab === "customers";
  const showVisibilityToggle = activeTab === "prospects";
  const showProspectSampleFilter = activeTab === "prospects";
  const showProspectBulkSelect = activeTab === "prospects";
  const showCustomerBulkSelect = activeTab === "customers";
  const showBulkSelectCheckbox =
    showProspectBulkSelect || showCustomerBulkSelect;
  const secondaryVisibilityValue = "ignored";
  const secondaryVisibilityLabel = "Ignored";

  if (buyersLoading) {
    return (
      <div className="py-4 text-center text-muted">
        <Spinner className="me-2" />
        Loading…
      </div>
    );
  }

  const ppmOptions = [
    { value: "__none__", label: "— (no PPM)" },
    ...buyerPpmOptions.map((n) => ({
      value: String(n),
      label: String(n),
    })),
  ];

  const cityOptions = buyerCityOptions.map((city) => ({
    value: city,
    label: city,
  }));

  const stateOptions = buyerStateOptions.map((state) => ({
    value: state,
    label: state,
  }));

  const tableColSpan = showContactFilters
    ? 5
    : (showBulkSelectCheckbox ? 1 : 0) +
      4 +
      (showStatusColumn ? 1 : 0) +
      (showProfileTypeColumn ? 1 : 0) +
      (showPpmColumn ? 1 : 0) +
      (showDealerColumn ? 1 : 0) +
      (showLeadSourceColumn ? 1 : 0) +
      (showAcquiredColumn ? 1 : 0) +
      (showSampleDateColumn ? 1 : 0) +
      (showEngagementColumn ? 1 : 0) +
      (showSalesOwnerColumn ? 1 : 0) +
      (showLastFollowupColumn ? 1 : 0) +
      (showNextFollowupColumn ? 1 : 0) +
      (showLoginAttemptsColumn ? 1 : 0) +
      (showSessionsColumn ? 1 : 0) +
      (showProjectsColumn ? 1 : 0) +
      (showCustomerOrderMonthColumns
        ? 1 + ensureArray(customerOrderMonthKeys).length
        : 0) +
      (showRemarksInTable ? 1 : 0);

  const customerOrderHeaderRowSpan = showCustomerOrderMonthColumns
    ? { rowSpan: 2, className: "align-middle" }
    : {};

  const selectedBuyerIds = showCustomerBulkSelect
    ? selectedCustomerBuyerIds
    : selectedProspectBuyerIds;
  const setSelectedBuyerIds = showCustomerBulkSelect
    ? setSelectedCustomerBuyerIds
    : setSelectedProspectBuyerIds;

  const selectedIdSet = new Set(
    ensureArray(selectedBuyerIds).map((id) => Number(id))
  );
  const displayedRowIds = displayedRows
    .map((row) => Number(row.id))
    .filter((id) => Number.isFinite(id));
  const allDisplayedSelected =
    displayedRowIds.length > 0 &&
    displayedRowIds.every((id) => selectedIdSet.has(id));
  const someDisplayedSelected =
    displayedRowIds.some((id) => selectedIdSet.has(id)) && !allDisplayedSelected;

  const toggleBuyerRowSelection = (rowId, checked) => {
    const id = Number(rowId);
    if (!Number.isFinite(id)) return;
    setSelectedBuyerIds((prev) => {
      const next = new Set(ensureArray(prev).map((v) => Number(v)));
      if (checked) {
        next.add(id);
      } else {
        next.delete(id);
      }
      return Array.from(next);
    });
  };

  const toggleAllDisplayedBuyerSelection = (checked) => {
    if (!checked) {
      setSelectedBuyerIds((prev) => {
        const next = new Set(ensureArray(prev).map((v) => Number(v)));
        displayedRowIds.forEach((id) => next.delete(id));
        return Array.from(next);
      });
      return;
    }

    setSelectedBuyerIds((prev) => {
      const next = new Set(ensureArray(prev).map((v) => Number(v)));
      displayedRowIds.forEach((id) => next.add(id));
      return Array.from(next);
    });
  };

  return (
    <>
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
        <div className="d-flex flex-wrap align-items-center gap-2">
          {showContactFilters ? (
            <>
              <Input
                type="select"
                bsSize="sm"
                value={buyerContactStatusFilter}
                onChange={(e) => setBuyerContactStatusFilter(e.target.value)}
                className={STORE_DASHBOARD_FILTER_INPUT_CLASS}
                style={{ width: "140px" }}
              >
                <option value="">Status</option>
                <option value={CONTACT_SUB_STATUS_NONE}>-</option>
                <option value={CONTACT_SUB_STATUS_MARKETED}>Marketed</option>
                <option value={CONTACT_SUB_STATUS_ENQUIRED}>Enquired</option>
                <option value={CONTACT_SUB_STATUS_REJECTED}>Rejected</option>
                <option value={CONTACT_SUB_STATUS_NOT_INTERESTED}>
                  Not Interested
                </option>
              </Input>
              <Input
                type="select"
                bsSize="sm"
                value={buyerSourceFilter}
                onChange={(e) => setBuyerSourceFilter(e.target.value)}
                className={STORE_DASHBOARD_FILTER_INPUT_CLASS}
                style={{ width: "160px" }}
              >
                <option value="">Source</option>
                {contactSourceOptions.map((source) => (
                  <option key={source} value={source}>
                    {source}
                  </option>
                ))}
              </Input>
            </>
          ) : null}

          {showContactFilters ? (
            <Input
              bsSize="sm"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder="Buyer name, buyer user name, mobile, id"
              style={{ width: "350px" }}
              className={`${STORE_DASHBOARD_FILTER_INPUT_CLASS} fs-6 p-2`}
            />
          ) : null}

          {showProspectPipelineFilters || showLeadCityFilter || showContactFilters ? (
            <StoreMultiSelectFilter
              key={`buyer-state-filter-${activeTab}`}
              label="State"
              options={stateOptions}
              value={buyerStateFilter}
              onChange={setBuyerStateFilter}
              minWidth="140px"
            />
          ) : null}

          {showProspectPipelineFilters || showLeadCityFilter || showContactFilters ? (
            <StoreMultiSelectFilter
              key={`buyer-city-filter-${activeTab}`}
              label="City"
              options={cityOptions}
              value={buyerCityFilter}
              onChange={setBuyerCityFilter}
              minWidth="140px"
            />
          ) : null}

          {showCustomerFilters ? (
            <StoreMultiSelectFilter
              label="Acquired month"
              options={buyerAcquiredMonthFilterOptions}
              value={buyerAcquiredMonthFilter}
              onChange={setBuyerAcquiredMonthFilter}
              minWidth="150px"
            />
          ) : null}

          {showProspectPipelineFilters ? (
            <>
              <StoreMultiSelectFilter
                label="PPM"
                options={ppmOptions}
                value={buyerPpmFilter}
                onChange={setBuyerPpmFilter}
                minWidth="120px"
              />
              <StoreMultiSelectFilter
                label="Engagement"
                options={engagementFilterOptions}
                value={buyerEngagementFilter}
                onChange={setBuyerEngagementFilter}
                minWidth="150px"
              />
              <StoreMultiSelectFilter
                label="Sales owner"
                options={buyerSalesOwnerFilterOptions}
                value={buyerSalesOwnerFilter}
                onChange={setBuyerSalesOwnerFilter}
                minWidth="150px"
              />
              <StoreMultiSelectFilter
                label="Fit"
                options={STORE_BUYER_FIT_FILTER_OPTIONS}
                value={profileFitFilter}
                onChange={setProfileFitFilter}
                minWidth="120px"
              />
              <StoreMultiSelectFilter
                label="Conviction"
                options={STORE_BUYER_CONVICTION_FILTER_OPTIONS}
                value={convictionFilter}
                onChange={setConvictionFilter}
                minWidth="120px"
              />
              <StoreMultiSelectFilter
                label="Committed date"
                options={committedDateFilterOptions}
                value={committedDateFilter}
                onChange={setCommittedDateFilter}
                minWidth="150px"
              />
            </>
          ) : null}
        </div>
        <div className="d-flex flex-wrap align-items-center gap-2">
          {showContactFilters ? (
            <>
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm"
                onClick={onContactCsvButtonClick}
                disabled={importCsvLoading}
                title="CSV: Mobile, Name (→ BuyerUser.name), Company (→ Buyer.name), Source, City, Profile Type, Status. Status maps to contact sub_status. Existing mobiles are skipped. Invalid rows roll back the file."
              >
                {importCsvLoading ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm me-1"
                      role="status"
                      aria-hidden="true"
                    />
                    Importing...
                  </>
                ) : (
                  "Import CSV"
                )}
              </button>
              <input
                type="file"
                ref={contactCsvInputRef}
                accept=".csv"
                onChange={onContactCsvFileChange}
                style={{ display: "none" }}
              />
            </>
          ) : null}
          {showVisibilityToggle ? (
            <div
              className="btn-group btn-group-sm"
              role="group"
              aria-label="Buyer visibility filter"
            >
              <button
                type="button"
                className={`btn ${
                  buyerVisibilityFilter === "all"
                    ? "btn-primary"
                    : "btn-outline-primary"
                }`}
                onClick={() => setBuyerVisibilityFilter("all")}
              >
                All
              </button>
              <button
                type="button"
                className={`btn ${
                  buyerVisibilityFilter === "active"
                    ? "btn-primary"
                    : "btn-outline-primary"
                }`}
                onClick={() => setBuyerVisibilityFilter("active")}
              >
                Active
              </button>
              {showProspectSampleFilter ? (
                <button
                  type="button"
                  className={`btn ${
                    buyerVisibilityFilter === "sample"
                      ? "btn-primary"
                      : "btn-outline-primary"
                  }`}
                  onClick={() => setBuyerVisibilityFilter("sample")}
                >
                  Sample
                </button>
              ) : null}
              <button
                type="button"
                className={`btn ${
                  buyerVisibilityFilter === secondaryVisibilityValue
                    ? "btn-primary"
                    : "btn-outline-primary"
                }`}
                onClick={() =>
                  setBuyerVisibilityFilter(secondaryVisibilityValue)
                }
              >
                {secondaryVisibilityLabel}
              </button>
            </div>
          ) : null}
        </div>
      </div>
      <>
        <div className="d-flex justify-content-between align-items-center mb-2">
          <div className="small text-muted">
            {displayedRows.length} {displayedRows.length === 1 ? "row" : "rows"}
            {showCustomerBulkSelect && selectedIdSet.size > 0
              ? ` · totals for ${selectedIdSet.size} selected`
              : null}
          </div>
          <div className="d-flex align-items-center gap-2">
            {showProspectBulkSelect && selectedIdSet.size > 0 ? (
              <button
                type="button"
                className="btn btn-outline-primary btn-sm"
                onClick={() =>
                  onOpenBulkEditBuyerModal?.(Array.from(selectedIdSet))
                }
              >
                Bulk Update ({selectedIdSet.size})
              </button>
            ) : null}
            {showRemarksToggle ? (
            <div
              className="btn-group btn-group-sm"
              role="group"
              aria-label="Buyer table view"
            >
              <button
                type="button"
                className={`btn ${
                  buyersTableViewMode === "compact"
                    ? "btn-primary"
                    : "btn-outline-primary"
                }`}
                onClick={() => setBuyersTableViewMode("compact")}
              >
                Compact
              </button>
              <button
                type="button"
                className={`btn ${
                  buyersTableViewMode === "remarks"
                    ? "btn-primary"
                    : "btn-outline-primary"
                }`}
                onClick={() => setBuyersTableViewMode("remarks")}
              >
                Remarks
              </button>
              <button
                type="button"
                className={`btn ${
                  buyersTableViewMode === "wide"
                    ? "btn-primary"
                    : "btn-outline-primary"
                }`}
                onClick={() => setBuyersTableViewMode("wide")}
              >
                Wide
              </button>
            </div>
            ) : null}
          </div>
        </div>

        <Table bordered hover responsive className="mb-0 store-sales-table">
          <thead className="store-sales-table__head">
            <tr>
              {showContactFilters ? (
                <>
                  <th role="button" onClick={() => handleSort("name")}>
                    Name {renderSortIcon("name")}
                  </th>
                  <th role="button" onClick={() => handleSort("city")}>
                    City {renderSortIcon("city")}
                  </th>
                  <th role="button" onClick={() => handleSort("profileType")}>
                    Profile Type {renderSortIcon("profileType")}
                  </th>
                  <th role="button" onClick={() => handleSort("leadSource")}>
                    Source {renderSortIcon("leadSource")}
                  </th>
                  <th
                    role="button"
                    onClick={() => handleSort("contactStatusLabel")}
                  >
                    Status {renderSortIcon("contactStatusLabel")}
                  </th>
                </>
              ) : (
                <>
                  {showBulkSelectCheckbox ? (
                    <th className="text-center" {...customerOrderHeaderRowSpan}>
                      <Input
                        type="checkbox"
                        className="form-check-input mt-0"
                        checked={allDisplayedSelected}
                        ref={(el) => {
                          if (el) el.indeterminate = someDisplayedSelected;
                        }}
                        onChange={(e) =>
                          toggleAllDisplayedBuyerSelection(e.target.checked)
                        }
                        aria-label={
                          showCustomerBulkSelect
                            ? "Select all customers"
                            : "Select all prospects"
                        }
                      />
                    </th>
                  ) : null}
                  <th
                    role="button"
                    onClick={() => handleSort("name")}
                    {...customerOrderHeaderRowSpan}
                  >
                    Name {renderSortIcon("name")}
                  </th>
                  {showStatusColumn ? (
                    <th
                      role="button"
                      onClick={() => handleSort("status")}
                      className="text-capitalize"
                      {...customerOrderHeaderRowSpan}
                    >
                      Status {renderSortIcon("status")}
                    </th>
                  ) : null}
                  <th
                    role="button"
                    onClick={() => handleSort("city")}
                    {...customerOrderHeaderRowSpan}
                  >
                    City {renderSortIcon("city")}
                  </th>
                  {showProfileTypeColumn ? (
                    <th
                      role="button"
                      onClick={() => handleSort("profileType")}
                      {...customerOrderHeaderRowSpan}
                    >
                      Profile Type {renderSortIcon("profileType")}
                    </th>
                  ) : null}
                  {showPpmColumn ? (
                    <th className="text-center" {...customerOrderHeaderRowSpan}>
                      PPM
                    </th>
                  ) : null}
                  {showDealerColumn ? (
                    <th {...customerOrderHeaderRowSpan}>Dealer</th>
                  ) : null}
                  {showLeadSourceColumn ? (
                    <th
                      role="button"
                      onClick={() => handleSort("leadSource")}
                      {...customerOrderHeaderRowSpan}
                    >
                      {leadSourceColumnLabel} {renderSortIcon("leadSource")}
                    </th>
                  ) : null}
                  {showAcquiredColumn ? (
                    <th
                      role="button"
                      onClick={() => handleSort("acquiredAt")}
                      {...customerOrderHeaderRowSpan}
                    >
                      Acquired {renderSortIcon("acquiredAt")}
                    </th>
                  ) : null}
                  {showSampleDateColumn ? (
                    <th
                      role="button"
                      onClick={() => handleSort("sampleAcceptedAt")}
                      {...customerOrderHeaderRowSpan}
                    >
                      Sample Date {renderSortIcon("sampleAcceptedAt")}
                    </th>
                  ) : null}
                  <th
                    role="button"
                    onClick={() => handleSort(signedUpColumnSortKey)}
                    {...customerOrderHeaderRowSpan}
                  >
                    {signedUpColumnLabel} {renderSortIcon(signedUpColumnSortKey)}
                  </th>
                  {showLoginAttemptsColumn && !loginAttemptsColumnLast ? (
                    <th
                      role="button"
                      onClick={() => handleSort("signupStartCount")}
                      {...customerOrderHeaderRowSpan}
                    >
                      Login Attempts {renderSortIcon("signupStartCount")}
                    </th>
                  ) : null}
                  <th
                    role="button"
                    onClick={() => handleSort("lastActivityAt")}
                    {...customerOrderHeaderRowSpan}
                  >
                    Last Activity {renderSortIcon("lastActivityAt")}
                  </th>
                  {showProjectsColumn ? (
                    <th
                      role="button"
                      onClick={() => handleSort("projectsCount")}
                      {...customerOrderHeaderRowSpan}
                    >
                      Projects {renderSortIcon("projectsCount")}
                    </th>
                  ) : null}
                  {showSessionsColumn ? (
                    <th
                      role="button"
                      onClick={() => handleSort("sessionsCount")}
                      {...customerOrderHeaderRowSpan}
                    >
                      Sessions {renderSortIcon("sessionsCount")}
                    </th>
                  ) : null}
                  {showLoginAttemptsColumn && loginAttemptsColumnLast ? (
                    <th
                      role="button"
                      onClick={() => handleSort("signupStartCount")}
                      {...customerOrderHeaderRowSpan}
                    >
                      Login Attempts {renderSortIcon("signupStartCount")}
                    </th>
                  ) : null}
                  {showEngagementColumn ? (
                    <th {...customerOrderHeaderRowSpan}>Engagement</th>
                  ) : null}
                  {showSalesOwnerColumn ? (
                    <th {...customerOrderHeaderRowSpan}>Sales owner</th>
                  ) : null}
                  {showLastFollowupColumn ? (
                    <th
                      role="button"
                      onClick={() => handleSort("lastFollowupAt")}
                      {...customerOrderHeaderRowSpan}
                    >
                      Last follow-up {renderSortIcon("lastFollowupAt")}
                    </th>
                  ) : null}
                  {showNextFollowupColumn ? (
                    <th
                      role="button"
                      onClick={() => handleSort("followupDate")}
                      {...customerOrderHeaderRowSpan}
                    >
                      Next follow-up {renderSortIcon("followupDate")}
                    </th>
                  ) : null}
                  {showCustomerOrderMonthColumns ? (
                    <th
                      role="button"
                      onClick={() => handleSort("customerOrderTotal")}
                      className="text-center"
                    >
                      <div className="d-flex flex-column align-items-center">
                        <div>
                          Total {renderSortIcon("customerOrderTotal")}
                        </div>
                      </div>
                    </th>
                  ) : null}
                  {showCustomerOrderMonthColumns
                    ? ensureArray(customerOrderMonthKeys).map((monthKey) => (
                        <th
                          key={monthKey}
                          role="button"
                          onClick={() =>
                            handleSort(`customerOrderMonth_${monthKey}`)
                          }
                          className="text-center"
                        >
                          <div className="d-flex flex-column align-items-center">
                            <div>
                              {formatCustomerOrderMonthHeader(monthKey)}{" "}
                              {renderSortIcon(`customerOrderMonth_${monthKey}`)}
                            </div>
                          </div>
                        </th>
                      ))
                    : null}
                  {showRemarksInTable ? (
                    <th
                      className="store-sales-table__remarks-col"
                      {...customerOrderHeaderRowSpan}
                    >
                      Remarks
                    </th>
                  ) : null}
                </>
              )}
            </tr>
            {showCustomerOrderMonthColumns ? (
              <tr>
                <th className="text-center fw-normal">
                  <CustomerOrderHeaderTotal
                    bucket={buyerOrderMonthStats?.grandTotal}
                    loading={customerOrdersLoading}
                  />
                </th>
                {ensureArray(customerOrderMonthKeys).map((monthKey) => (
                  <th key={`total-${monthKey}`} className="text-center fw-normal">
                    <CustomerOrderHeaderTotal
                      bucket={buyerOrderMonthStats?.monthTotals?.[monthKey]}
                      loading={customerOrdersLoading}
                    />
                  </th>
                ))}
              </tr>
            ) : null}
          </thead>
          <tbody>
            {displayedRows.length === 0 ? (
              <tr>
                <td
                  colSpan={tableColSpan}
                  className="text-muted text-center py-4"
                >
                  No buyers found.
                </td>
              </tr>
            ) : (
              displayedRows.map((row) => (
                  <tr
                    key={row.id}
                    data-store-dashboard-buyer-row={row.id}
                    className={
                      highlightBuyerId != null &&
                      highlightBuyerId === Number(row.id)
                        ? "store-dashboard-buyer-row--highlight"
                        : undefined
                    }
                    onClick={() =>
                      showContactFilters
                        ? handleOpenBuyer(row)
                        : openEditBuyerModal(row)
                    }
                    style={{ cursor: "pointer" }}
                  >
                    {showContactFilters ? (
                      <>
                        <td>
                          <BuyerDashboardNameCell row={row} />
                        </td>
                        <td>{row.cityLabel}</td>
                        <td className="text-capitalize">{row.profileType}</td>
                        <td>{row.leadSource}</td>
                        <td>{row.contactStatusLabel}</td>
                      </>
                    ) : (
                      <>
                        {showBulkSelectCheckbox ? (
                          <td
                            className="text-center"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Input
                              type="checkbox"
                              className="form-check-input mt-0"
                              checked={selectedIdSet.has(Number(row.id))}
                              onChange={(e) =>
                                toggleBuyerRowSelection(
                                  row.id,
                                  e.target.checked
                                )
                              }
                              aria-label={`Select buyer ${row.id}`}
                            />
                          </td>
                        ) : null}
                        <td onClick={(e) => e.stopPropagation()}>
                          <BuyerDashboardNameCell
                            row={row}
                            showNotifications
                            onOpenNotifications={openNotificationsForName}
                          />
                        </td>
                        {showStatusColumn ? (
                          <td className="text-capitalize">
                            {row.status || "-"}
                          </td>
                        ) : null}
                        <td>{row.cityLabel}</td>
                        {showProfileTypeColumn ? (
                          <td className="text-capitalize">{row.profileType}</td>
                        ) : null}
                        {showPpmColumn ? (
                          <td className="text-center">
                            {row.projectsPerMonth == null
                              ? "-"
                              : String(row.projectsPerMonth)}
                          </td>
                        ) : null}
                        {showDealerColumn ? <td>{row.dealerName}</td> : null}
                        {showLeadSourceColumn ? (
                          <td>{row.leadSource}</td>
                        ) : null}
                        {showAcquiredColumn ? (
                          <td title={fmtHoverDateTime(row.acquiredAt)}>
                            {row.acquiredAt
                              ? fmtRelativeTime(row.acquiredAt)
                              : "-"}
                          </td>
                        ) : null}
                        {showSampleDateColumn ? (
                          <td title={fmtHoverDateTime(row.sampleAcceptedAt)}>
                            {row.sampleAcceptedAt
                              ? fmtRelativeTime(row.sampleAcceptedAt)
                              : "-"}
                          </td>
                        ) : null}
                        <td
                          title={fmtHoverDateTime(
                            activeTab === "leads" ? row.createdAt : row.signedUpAt
                          )}
                        >
                          {fmtRelativeTime(
                            activeTab === "leads" ? row.createdAt : row.signedUpAt
                          )}
                        </td>
                        {showLoginAttemptsColumn && !loginAttemptsColumnLast ? (
                          <td className="text-center">
                            {row.signupStartCount > 0
                              ? String(row.signupStartCount)
                              : "-"}
                          </td>
                        ) : null}
                        <td
                          title={fmtHoverDateTime(row.lastActivityAt)}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOpenBuyer(row);
                          }}
                        >
                          {row.lastActivityAt ? (
                            <div className="d-flex flex-wrap align-items-baseline gap-1">
                              <span>{fmtRelativeTime(row.lastActivityAt)}</span>
                              <button
                                type="button"
                                className="btn btn-link btn-sm p-0 align-baseline"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openActivitiesPaneForBuyer(row, {
                                    initialTab: "activities",
                                  });
                                }}
                              >
                                {`(${activityCountPhrase(
                                  row.activitiesCount
                                )})`}
                              </button>
                            </div>
                          ) : (
                            "-"
                          )}
                        </td>
                        {showProjectsColumn ? (
                          <td>
                            <BuyerProjectsCell
                              row={row}
                              onOpenBuyer={handleOpenBuyer}
                            />
                          </td>
                        ) : null}
                        {showSessionsColumn ? (
                          <td
                            onClick={(e) => {
                              e.stopPropagation();
                              handleOpenBuyer(row);
                            }}
                          >
                            {row.sessionsCount > 0 ? (
                              <button
                                type="button"
                                className="btn btn-link btn-sm p-0 align-baseline"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openActivitiesPaneForBuyer(row, {
                                    initialTab: "sessions",
                                  });
                                }}
                              >
                                {sessionCountPhrase(row.sessionsCount)}
                              </button>
                            ) : (
                              "-"
                            )}
                          </td>
                        ) : null}
                        {showLoginAttemptsColumn && loginAttemptsColumnLast ? (
                          <td className="text-center">
                            {row.signupStartCount > 0
                              ? String(row.signupStartCount)
                              : "-"}
                          </td>
                        ) : null}
                        {showEngagementColumn ? (
                          <td className="text-capitalize">
                            {row.engagementStatus || "-"}
                          </td>
                        ) : null}
                        {showSalesOwnerColumn ? (
                          <td>{row.salesOwnerName}</td>
                        ) : null}
                        {showLastFollowupColumn ? (
                          <td title={fmtHoverDateTime(row.lastFollowupAt)}>
                            {row.lastFollowupAt
                              ? fmtRelativeTime(row.lastFollowupAt)
                              : "-"}
                          </td>
                        ) : null}
                        {showNextFollowupColumn ? (
                          <td title={fmtHoverDate(row.followupDate)}>
                            {row.followupDate ? fmtHoverDate(row.followupDate) : "-"}
                          </td>
                        ) : null}
                        {showCustomerOrderMonthColumns ? (
                          <td className="text-end">
                            <CustomerOrderValueCell
                              bucket={row.customerOrderStats?.total}
                            />
                          </td>
                        ) : null}
                        {showCustomerOrderMonthColumns
                          ? ensureArray(customerOrderMonthKeys).map((monthKey) => (
                              <td key={monthKey} className="text-end">
                                <CustomerOrderValueCell
                                  bucket={row.customerOrderStats?.months?.[monthKey]}
                                />
                              </td>
                            ))
                          : null}
                        {showRemarksInTable ? (
                          <td className="small store-sales-table__remarks-col">
                            <BuyerRemarksCell salesNotes={row.salesNotes} />
                          </td>
                        ) : null}
                      </>
                    )}
                  </tr>
              ))
            )}
          </tbody>
        </Table>
      </>
    </>
  );
};

export default StoreSalesDashboardBuyersSection;
